import React, { FC, useCallback } from 'react';
import translate from '@translate';
import BodyTopBlock, { ActionType } from 'src/components/BodyTopBlock';
import { ExportTypeType, SelectRowType } from 'src/types/table';
import ExportTableAction from 'src/components/ExportTableAction';
import StoreIcon from '@mui/icons-material/Store';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTabs } from '../ProductsListContainer/TopBlock';
import { AddIcon, InventoryIcon, ReloadIcon } from 'src/components/SkladonIcon/Icons';
import { EditIcon, HideIcon, ImportIcon, ShowIcon } from '../../components/SkladonIcon/SmallIcons';
import { ProductType } from '../../types/product';
import { useCheckPermission } from '../../utils/checkPermission';

type TopBlockProps = {
    allProviders: boolean;
    setAllProviders: (value: boolean) => void;
    reloadTableData: () => void;
    reloadProductsList: () => void;
    permissionCreate: boolean;
    permissionUpdate: boolean;
    permissionAllProviders: boolean;
    permissionDcixImportProducts: boolean;
    permissionDcixImportEans: boolean;
    selectedRows: SelectRowType[];
    filter: string;
    handleActivateProducts: () => void;
    handleDeactivateProducts: () => void;
    handleExport: (type: ExportTypeType) => void;
    handleSyncProductsToDcixFetch: () => void;
    handleMassUpdate: () => void;
    handleSyncEansToDcixFetch: () => void;
    productsData: ProductType[]
};

const TopBlock: FC<TopBlockProps> = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const permissionExportProducts = useCheckPermission('PRODUCT.EXPORT');

    const selected = location.pathname.split('/')[2];

    const handleChange = useCallback((newUrl: string) => {
        navigate('/products/' + newUrl);
    }, []);

    const handleNew = useCallback(() => {
        navigate(`/products/new`);
    }, []);

    const handleImport = () => {
        navigate(`/products/import`);
    };

    const handleShowStock = () => {
        navigate(`/products/${selected}/withStock`);
    };

    const handleShowAllProviders = () => {
        navigate(props.allProviders ? `/products/${selected}` : `/products/${selected}/all`, { replace: true });    };

    const actions: ActionType[] = [
        {
            onClick: handleShowStock,
            icon: <InventoryIcon />,
            tooltip: translate('products.action.withStock'),
            variant: 'outlined',
            active: false,
        },
        {
            onClick: () => handleShowAllProviders(),
            icon: <StoreIcon />,
            tooltip: translate('orders.allProviders'),
            variant: 'outlined',
            active: props.allProviders,
            hidden: !props.permissionAllProviders,
        },
        {
            onClick: () => props.reloadTableData(),
            icon: <ReloadIcon />,
            tooltip: translate('products.action.synchronizeTable'),
            variant: 'outlined',
        },
        {
            items: [
                {
                    onClick: handleImport,
                    icon: <ImportIcon />,
                    label: translate('products.action.importProducts'),
                    variant: 'text',
                    hidden: !props.permissionCreate || !props.permissionUpdate,
                },
                {
                    node: (
                        <ExportTableAction
                            title={translate('products.action.dowloadProductsTable')}
                            handleExport={props.handleExport}
                            textWithIcon
                        />
                    ),
                    hidden: !permissionExportProducts,
                },
                {
                    label: translate('products.action.activateProducts'),
                    onClick: props.handleActivateProducts,
                    icon: <ShowIcon />,
                    hidden:
                        props.filter !== 'inactive' || !props.permissionUpdate || !props.selectedRows.length,
                    variant: 'text',
                },
                {
                    label: translate('products.action.deactivateProducts'),
                    onClick: props.handleDeactivateProducts,
                    icon: <HideIcon />,
                    hidden: props.filter !== 'active' || !props.permissionUpdate || !props.selectedRows.length,
                    variant: 'text',
                },
                {
                    label: translate('products.action.syncProductsToDcix'),
                    onClick: props.handleSyncProductsToDcixFetch,
                    icon: <ReloadIcon />,
                    hidden: !props.permissionDcixImportProducts,
                    variant: 'text',
                },
                {
                    label: translate('products.action.syncEansToDcix'),
                    onClick: props.handleSyncEansToDcixFetch,
                    icon: <ReloadIcon />,
                    hidden: !props.permissionDcixImportEans,
                    variant: 'text',
                },
                {
                    onClick: props.handleMassUpdate,
                    icon: <EditIcon />,
                    label: translate('products.action.massUpdate'),
                    variant: 'text',
                    hidden: !props.permissionUpdate || !props.selectedRows.length || props.selectedRows.some(rowId => {
                        const product = props.productsData.find(product => product.ID === Number(rowId));
                        return product && product.PRODUCT_TYPE === 'pack';
                    }),
                },
            ],
        },
        {
            label: translate('products.createNew'),
            onClick: handleNew,
            icon: <AddIcon />,
            hidden: !props.permissionCreate,
        },
    ];

    return <BodyTopBlock tabs={getTabs()} selectTab={selected} onChangeTab={handleChange} actions={actions} />;
};

export default TopBlock;
